<template>
  <section id="user-account-access" class="my-8">
    <v-row class="d-flex justify-center">
      <v-col cols="12" md="10" lg="10">
        <card-widget>
          <template #cardBody>
            <loader :loading="isLoading" :size="$appConfig.loader.small" />

            <v-row no-gutters align="start">
              <v-col cols="12" lg="3" md="3">
                <h5
                  class="
                    text-h6
                    secondary--font
                    font-weight-large
                    text--primary
                    pb-6
                  "
                >
                  {{ $dictionary.app.manageUsers.user.accountAccess.title }}
                </h5>
              </v-col>
              <v-col
                cols="12"
                lg="9"
                md="9"
                class="d-flex justify-space-between justify-end overflow-auto"
              >
                <debounce-search-text-field
                  prop-to-search="email"
                  placeholder="Search Users..."
                  :sort-by="sortConfig.sortBy"
                  :sort-order="sortConfig.sortOrder"
                  :lazy-search="getUserAccountsList"
                  :no-of-records="accountsParams.per_page"
                  @search-value="userSearch = $event"
                  :custom-search-param="emailSearchParam"
                />

                <v-select
                  label="Change Role"
                  dense
                  outlined
                  @change="onRolesChange"
                  :disabled="!hasSelectedUsers"
                  :items="roles"
                  v-model="user.roles"
                  class="px-2"
                  :menu-props="{ bottom: true, offsetY: true }"
                  :no-data-text="$dictionary.app.common.roles.noData"
                >
                  <template #item="{ on, attrs, item }">
                    <v-list-item v-on="on" v-bind="attrs">
                      <v-list-item-content class="body-2 text--primary">
                        {{ item }}
                        <v-spacer />
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>

                <v-btn
                  outlined
                  :disabled="!hasSelectedUsers"
                  @click="onDeleteUser(true)"
                  height="40px"
                  class="font-weight-bold mr-2"
                  :class="{
                    'btn-light-grey-border text--secondary': hasSelectedUsers,
                  }"
                >
                  <v-icon v-text="'mdi-delete'" class="pr-3 text--secondary" />
                  Delete
                </v-btn>

                <v-btn
                  outlined
                  @click="addUserDrawer = true"
                  height="40px"
                  class="btn-light-grey-border text--secondary font-weight-bold"
                >
                  <v-icon class="pr-3 text--secondary" v-text="'mdi-plus'" />
                  Add User
                </v-btn>
              </v-col>
            </v-row>

            <account-users-table
              :users="accounts"
              :is-loading="areAccountsLoading"
              @account-selected="updateSelectedAccounts"
              @get-records="getUserAccountsList"
              default-sort-order="email"
              :reset-selected-accounts="resetSelectedUsers"
            />
          </template>
        </card-widget>
      </v-col>
    </v-row>
    <delete-record-dialog
      :dialog="deleteUserDialog.dialog"
      :sub-title="deleteUserDialog.subTitle"
      :title="deleteUserDialog.title"
      @close="updateDeleteUserDialog(false)"
      @delete="deleteUserApi"
      :is-loading="isUserDeleting"
    />

    <delete-record-dialog
      :dialog="updateUserAccountsDialog.dialog"
      :sub-title="updateUserAccountsDialog.subTitle"
      :title="updateUserAccountsDialog.title"
      @close="onUpdateUserDialogClose"
      @delete="updateUserAccountsRole(updateUsersRolePayload, actions.update)"
      :is-loading="isUserUpdating"
      btn-text="Save"
    />

    <add-user-form
      :user-data="user"
      @user-added="onAddNewuser"
      @toogle-drawer="toggleDrawer"
      :add-user-drawer="addUserDrawer"
      @close-nav-drawer="toggleDrawer"
      @get-users="fetchUsers = !fetchUsers"
    >
      <template #roles>
        <v-row>
          <v-col cols="12" lg="12" md="12">
            <div class="form-control">
              <div class="pb-6">
                <label
                  for="user-role"
                  class="overline font-weight-large"
                  v-text="'USER ACCESS'"
                />
              </div>

              <v-select
                outlined
                label="Role"
                :items="roles"
                id="user-role"
                class="required"
                v-model="newUserRole"
                :rules="[required('Role')]"
                item-color="primary custom-dropdown"
              />
            </div>
          </v-col>
        </v-row>
      </template>
    </add-user-form>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { CardWidget } from "@/components/widgets";
import AccountUsersTable from "@/components/tables/AccountUsersTable.vue";

import {
  DeleteRecordDialog,
  DebounceSearchTextField,
} from "@/components/shared";
import {
  hasProp,
  isEmpty,
  updateStore,
  isResponseCode,
  getPropArr,
} from "@/utils/common.utils";

import { capitalize } from "@/filters";
import AddUserForm from "@/components/forms/AddUserForm.vue";

import { SharedUtilsMixin } from "@/mixins";
import { RESPONSE_CODES, ROLES, SORTING_ORDERS } from "@/constants";
import {
  deleteAccountUsers,
  getAccountUsers,
  updateAccountUsers,
} from "@/services/account";
import { required } from "@/validators/form-validators";

/**
 * Account User access details
 */
export default {
  name: "AccountUsers",
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [SharedUtilsMixin],
  /**
   * ---------------- Props ------------------
   */
  props: {
    user: { type: Object, required: true },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    CardWidget,
    AddUserForm,
    AccountUsersTable,
    DeleteRecordDialog,
    DebounceSearchTextField,
  },
  /**
   * ---------------- Data Properties ------------------
   */
  data() {
    const { snackbarTypes } = this.$appConfig.snackbar;
    const {
      deleteUser,
      updateUser,
    } = this.$dictionary.app.manageUsers.user.accountAccess;

    return {
      fetchUsers: false,
      snackbarTypes,
      accounts: [],
      isLoading: false,
      areAccountsLoading: false,
      selectedUsers: [],
      isFormValid: false,
      deleteUserDialog: {
        dialog: false,
        title: deleteUser.title,
        subTitle: deleteUser.subTitle,
      },
      updateUserAccountsDialog: {
        dialog: false,
        title: updateUser.title,
        multipleTitle: updateUser.multipleTitle,
        subTitle: updateUser.subTitle,
      },
      addUserDrawer: false,
      isUserDeleting: false,
      isUserUpdating: false,
      actions: {
        add: "add",
        update: "update",
      },
      resetSelectedUsers: false,
      accountsParams: {},
      userSearch: "",
      newUserRole: capitalize(ROLES.user),
      emailSearchParam: "q[member_of_User_type_email_cont]",
    };
  },
  /**
   * ---------------- Computed Properties ------------------
   */
  computed: {
    ...mapGetters({
      roles: "app/rolesCapitalized",
    }),

    /**
     * Sorting and searching config of user accounts
     * @type {Object}
     */
    sortConfig() {
      return this.getSortConfig("accountsParams", SORTING_ORDERS.asc);
    },
    /**
     * Id of an account whose users are being viewed
     */
    accountId() {
      return this.$route.params.id;
    },
    /**
     * Checks if there are selected users for an account
     * @type {Boolean}
     */
    hasSelectedUsers() {
      return !isEmpty(this.selectedUsers);
    },
    /**
     * Remove membership request payload
     */
    removeMembershipPayload() {
      return { member_ids: getPropArr(this.selectedUsers, "id") };
    },
    /**
     * Update membership request payload
     */
    updateUsersRolePayload() {
      return {
        memberships: this.selectedUsers?.map(({ id: member_id }) => ({
          role: this.user.roles?.toLowerCase(),
          member_id,
        })),
      };
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    required,
    /**
     * Maps vuex actions in the component
     */
    ...mapActions({
      getRoles: "app/getRoles",
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * @listens user-added event when a user record have been created in GIP and Database
     */
    onAddNewuser({ id: member_id }) {
      const payload = {
        memberships: [{ member_id, role: this.newUserRole.toLowerCase() }],
      };
      this.updateUserAccountsRole(payload);
    },
    /**
     * Searching sort config
     */
    setSortParams(config, sortBy, sortOrder) {
      const searchProp = "member_of_User_type_field";
      config["q[s]"] = `${searchProp.replace("field", sortBy)} ${sortOrder}`;
    },
    /**
     * Toogles add user drawer
     */
    toggleDrawer(val) {
      this.addUserDrawer = val;
    },
    /**
     * Handles update user account access dialog properties
     * @listens close
     */
    onUpdateUserDialogClose() {
      this.updateUserAccountsDialog.dialog = false;
      this.user.roles = "";
    },
    /**
     * User accounts dropdown role change handler
     */
    onRolesChange(role) {
      let { updateUser } = this.$dictionary.app.manageUsers.user.accountAccess;
      if (this.hasSelectedUsers && role) {
        let title =
          this.selectedUsers?.length > 1
            ? this.updateUserAccountsDialog.multipleTitle
            : updateUser.title.replace(
                "{{accountName}}",
                this.selectedUsers[0]?.email
              );

        this.updateUserAccountsDialog.dialog = true;
        this.updateUserAccountsDialog.title = title;
        this.updateUserAccountsDialog.subTitle = this.updateUserAccountsDialog.subTitle.replace(
          "{{roleName}}",
          role
        );
      }
    },
    /**
     * Toogles fetchUsers state value to fetch the latest account users
     */
    updateUsers() {
      this.fetchUsers = !this.fetchUsers;
    },
    /**
     * Updates delete user dialog object
     */
    updateDeleteUserDialog(dialog, title = this.deleteUserDialog.title) {
      let newValue = { dialog, title };
      this.deleteUserDialog = updateStore(this.deleteUserDialog, newValue);
    },
    /**
     * Open delete user dialog
     */
    onDeleteUser() {
      const {
        deleteUser,
      } = this.$dictionary.app.manageUsers.user.accountAccess;
      const [{ email }] = this.selectedUsers;

      // Updates dialog title
      const title =
        this.selectedUsers?.length > 1
          ? deleteUser.title.replace("<<count>>", this.selectedUsers?.length)
          : deleteUser.singleUser.replace("{{email}}", email);

      this.updateDeleteUserDialog(true, title);
    },
    resetSelectedUsersArr() {
      this.selectedUsers = [];
      this.resetSelectedUsers = !this.resetSelectedUsers;
    },
    /**
     * Deletes user and its accounts access
     */
    async deleteUserApi() {
      try {
        this.isUserDeleting = true;
        const { status } = await deleteAccountUsers(
          this.$route.params.id,
          this.removeMembershipPayload
        );

        this.updateUsers();
        this.resetSelectedUsersArr();
        if (isResponseCode(status, RESPONSE_CODES.success)) {
          const {
            deleteUser,
          } = this.$dictionary.app.manageUsers.user.accountAccess;
          this.setSnackbar({
            value: true,
            message: deleteUser.successMsg,
            type: this.snackbarTypes.success,
          });
        }
      } finally {
        this.isUserDeleting = false;
      }
      // close delete user dialog
      this.updateDeleteUserDialog(false);
    },
    /**
     * Account access form submit handler
     * @todo Consume post api to update the user account access here
     * @listens submit
     */
    onSubmitHandler() {
      if (!this.isFormValid) return this.$refs.accountAccessForm.validate();
    },
    /**
     * Updates selected accounts
     * @listens account-selected
     */
    updateSelectedAccounts(accounts) {
      this.selectedUsers = accounts;
    },
    /**
     * Add users list sorting params
     */
    addSortParams(params) {
      const emailProp = "q[s]";
      const hasEmail = !hasProp(params, this.emailSearchParam);

      if (this.userSearch && hasEmail) {
        params[this.emailSearchParam] = this.userSearch;
      }

      if (hasProp(params, emailProp) && params[emailProp]) {
        const [sortBy, sortOrder] = params[emailProp].split(" ");
        switch (sortBy) {
          case "email":
            this.setSortParams(params, sortBy, sortOrder);
            break;
          case "name":
            this.setSortParams(params, "first_name", sortOrder);
            break;
          case "member_of_User_type_email":
            params["q[s]"] = `member_of_User_type_email ${sortOrder}`;
            break;
          default:
            if (sortBy) this.setSortParams(params, sortBy, sortOrder);
            break;
        }
      }
    },
    /**
     * Fetches account users list
     */
    async getUserAccountsList(params = this.accountsParams) {
      try {
        this.addSortParams(params);
        this.accountsParams = params;

        this.areAccountsLoading = true;
        const { data } = await getAccountUsers(
          this.accountId,
          this.accountsParams
        );

        this.accounts = data ?? [];
      } finally {
        this.areAccountsLoading = false;
      }
    },
    /**
     * Updates user account role
     * @description Sends (PUT) api call to update the user account access
     */
    async updateUserAccountsRole(payload = this.updateUsersRolePayload) {
      try {
        this.isUserUpdating = true;

        const { status } = await updateAccountUsers(
          this.$route.params.id,
          payload
        );

        if (isResponseCode(status, RESPONSE_CODES.success)) {
          this.setSnackbar({
            value: true,
            message: this.successMessage(this.actions.add),
            type: this.snackbarTypes.success,
          });
          this.resetSelectedUsersArr();
        }
        this.onUpdateUserDialogClose();
        this.updateUsers();
      } finally {
        this.isUserUpdating = false;
      }
    },
    /**
     * Computes account user membership updations or addition action success messages
     * @returns {String} Success message to be shown to the user in snackbar
     */
    successMessage(action) {
      const {
        addUserAccounts,
        updateUser,
      } = this.$dictionary.app.manageUsers.user.accountAccess;

      return action === this.actions.add
        ? addUserAccounts.successMsg
        : updateUser.successMsg;
    },
  },
  /**
   * ---------------- Watching properties ------------------
   */
  watch: {
    /**
     * Fetches new users list of an account on the changes of fetchUsers prop
     */
    fetchUsers() {
      this.getUserAccountsList();
    },
  },
  /**
   * ---------------- Created lifecycle hook ------------------
   */
  created() {
    this.getRoles();
  },
};
</script>
